import {
  createListCollection,
  Flex,
  HStack,
  Pagination as ChakraPagination,
  Text,
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { Button } from '../button';
import { IconButton } from '../button/icon-button';
import { Select } from '../select';
import { TextField } from '../text-field';
import {
  paginationBarActionsGap,
  paginationDefaultItemUnit,
  paginationDefaultPageSizeOptions,
  paginationDefaultSiblingCount,
  paginationGoButtonWidth,
  paginationItemGap,
  paginationJumpToInputWidth,
  paginationLabelColor,
  paginationMaxPagesWithoutEllipsis,
  paginationPageSizeSelectWidth,
  paginationSectionGap,
} from './pagination.kds';

export type PaginationRootProps = ChakraPagination.RootProps;

const PaginationRoot = React.forwardRef<HTMLDivElement, PaginationRootProps>(function PaginationRoot(props, ref) {
  const { type = 'button', siblingCount = paginationDefaultSiblingCount, ...rest } = props;

  return <ChakraPagination.Root ref={ref} siblingCount={siblingCount} type={type} {...rest} />;
});
PaginationRoot.displayName = 'PaginationRoot';

const PaginationItem = ChakraPagination.Item;
const PaginationEllipsis = ChakraPagination.Ellipsis;

const PaginationPrevTrigger = React.forwardRef<HTMLButtonElement, ChakraPagination.PrevTriggerProps>(
  function PaginationPrevTrigger(props, ref) {
    return (
      <ChakraPagination.PrevTrigger asChild ref={ref} {...props}>
        <IconButton aria-label="Previous page" colorPalette="neutral" icon={ChevronLeft} size="rg" variant="ghost" />
      </ChakraPagination.PrevTrigger>
    );
  }
);
PaginationPrevTrigger.displayName = 'PaginationPrevTrigger';

const PaginationNextTrigger = React.forwardRef<HTMLButtonElement, ChakraPagination.NextTriggerProps>(
  function PaginationNextTrigger(props, ref) {
    return (
      <ChakraPagination.NextTrigger asChild ref={ref} {...props}>
        <IconButton aria-label="Next page" colorPalette="neutral" icon={ChevronRight} size="rg" variant="ghost" />
      </ChakraPagination.NextTrigger>
    );
  }
);
PaginationNextTrigger.displayName = 'PaginationNextTrigger';

type PaginationPageList = Array<{ type: 'ellipsis' } | { type: 'page'; value: number }>;

const renderPageItems = (
  pages: PaginationPageList,
  totalPages: number,
  itemProps?: Readonly<React.HTMLAttributes<HTMLElement>>
) => {
  if (totalPages <= paginationMaxPagesWithoutEllipsis) {
    return Array.from({ length: totalPages }, (_, index) => {
      const pageNumber = index + 1;
      return (
        <PaginationItem key={`page-${pageNumber}`} type="page" value={pageNumber} {...itemProps}>
          {pageNumber}
        </PaginationItem>
      );
    });
  }

  let ellipsisOrdinal = 0;

  return pages.map((page, index) => {
    if (page.type === 'ellipsis') {
      const key = `ellipsis-${ellipsisOrdinal}`;
      ellipsisOrdinal += 1;
      return (
        <PaginationEllipsis index={index} key={key} {...itemProps}>
          …
        </PaginationEllipsis>
      );
    }

    return (
      <PaginationItem key={`page-${page.value}`} type="page" value={page.value} {...itemProps}>
        {page.value}
      </PaginationItem>
    );
  });
}

const PaginationItems = (props: Readonly<React.HTMLAttributes<HTMLElement>>) => (
  <ChakraPagination.Context>
    {({ pages, totalPages }) => (
      <HStack align="center" flexWrap="nowrap" gap={paginationItemGap}>
        {renderPageItems(pages, totalPages, props)}
      </HStack>
    )}
  </ChakraPagination.Context>
);

/** Prev trigger, page items, and next trigger in one row — matches Figma node 1178:266. */
const PaginationNav = (props: Readonly<React.HTMLAttributes<HTMLElement>>) => (
  <ChakraPagination.Context>
    {({ pages, totalPages }) => (
      <HStack align="center" flexWrap="nowrap" gap={paginationItemGap}>
        <PaginationPrevTrigger />
        {renderPageItems(pages, totalPages, props)}
        <PaginationNextTrigger />
      </HStack>
    )}
  </ChakraPagination.Context>
);

export interface PaginationBarProps {
  /** Current page (1-based). */
  page: number;
  /** Total item count across all pages. */
  count: number;
  /** Items per page. */
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Page-size dropdown options. */
  pageSizeOptions?: ReadonlyArray<{ value: number; label: string }>;
  /** Noun after the page-size control — default `results`. */
  itemUnit?: string;
  showJumpTo?: boolean;
  siblingCount?: number;
  /** Portal target for the page-size menu (e.g. modal content). */
  portalRef?: React.RefObject<HTMLElement | null>;
}

export const PaginationBar = ({
  page,
  count,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = paginationDefaultPageSizeOptions,
  itemUnit = paginationDefaultItemUnit,
  showJumpTo = false,
  siblingCount = paginationDefaultSiblingCount,
  portalRef,
}: Readonly<PaginationBarProps>) => {
  const [jumpToPage, setJumpToPage] = React.useState('');
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  const pageSizeCollection = React.useMemo(
    () =>
      createListCollection({
        items: pageSizeOptions.map(option => ({
          value: option.value.toString(),
          label: option.label,
        })),
      }),
    [pageSizeOptions]
  );

  const handleJumpToPage = () => {
    const pageNumber = Number.parseInt(jumpToPage, 10);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setJumpToPage('');
    }
  };

  const handleJumpToKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleJumpToPage();
    }
  };

  const jumpToValue = Number.parseInt(jumpToPage, 10);
  const isJumpToDisabled = !jumpToPage || Number.isNaN(jumpToValue) || jumpToValue < 1 || jumpToValue > totalPages;

  return (
    <Flex align="center" justify="space-between" py="4" w="full">
      <HStack align="center" flexShrink={0} flexWrap="nowrap" gap={paginationSectionGap}>
        <Text color={paginationLabelColor} flexShrink={0} textStyle="default.regular" whiteSpace="nowrap">
          Show
        </Text>
        <Select.Root
          collection={pageSizeCollection}
          size="sm"
          value={[pageSize.toString()]}
          onValueChange={({ value }) => {
            onPageSizeChange(Number(value[0]));
          }}
        >
          <Select.Label
            css={{
              border: '0',
              clip: 'rect(0 0 0 0)',
              h: '1px',
              m: '-1px',
              overflow: 'hidden',
              p: '0',
              position: 'absolute',
              w: '1px',
              whiteSpace: 'nowrap',
            }}
          >
            Page size
          </Select.Label>
          <Select.Trigger minW={paginationPageSizeSelectWidth}>
            <Select.ValueText />
          </Select.Trigger>
          <Select.Content portalRef={portalRef}>
            {pageSizeCollection.items.map(item => (
              <Select.Item item={item} key={item.value}>
                {item.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        <Text color={paginationLabelColor} flexShrink={0} textStyle="default.regular" whiteSpace="nowrap">
          {itemUnit} per page
        </Text>
      </HStack>

      <HStack align="center" flexShrink={0} flexWrap="nowrap" gap={paginationBarActionsGap}>
        {showJumpTo && (
          <HStack align="center" flexWrap="nowrap" gap={paginationSectionGap}>
            <Text color={paginationLabelColor} flexShrink={0} textStyle="default.regular" whiteSpace="nowrap">
              Jump to
            </Text>
            <TextField
              aria-label="Jump to page"
              max={totalPages}
              min={1}
              size="sm"
              type="number"
              value={jumpToPage}
              w={paginationJumpToInputWidth}
              onChange={event => setJumpToPage(event.target.value)}
              onKeyDown={handleJumpToKeyDown}
            />
            <Button
              colorPalette="neutral"
              disabled={isJumpToDisabled}
              flexShrink={0}
              minW={paginationGoButtonWidth}
              size="rg"
              type="button"
              variant="outline"
              w={paginationGoButtonWidth}
              onClick={handleJumpToPage}
            >
              Go
            </Button>
          </HStack>
        )}

        <PaginationRoot
          count={count}
          page={page}
          pageSize={pageSize}
          siblingCount={siblingCount}
          onPageChange={details => onPageChange(details.page)}
        >
          <PaginationNav />
        </PaginationRoot>
      </HStack>
    </Flex>
  );
};

export const Pagination = {
  Root: PaginationRoot,
  Nav: PaginationNav,
  PrevTrigger: PaginationPrevTrigger,
  NextTrigger: PaginationNextTrigger,
  Items: PaginationItems,
  Item: PaginationItem,
  Ellipsis: PaginationEllipsis,
};
